/**
 * Dates and times, written the same way everywhere: 2026-08-20, and 2026-08-20 16:41 when the time matters.
 * ISO order rather than the reader's locale, because the application is read in France and in Canada and 08/20
 * against 20/08 is a coin toss for half of them. The values themselves are local.
 */

const pad = (value: number) => String(value).padStart(2, '0');

function parts(value: string): { date: string; time: string } | null {
  const moment = new Date(value);

  if (Number.isNaN(moment.getTime())) {
    return null;
  }

  return {
    date: `${moment.getFullYear()}-${pad(moment.getMonth() + 1)}-${pad(moment.getDate())}`,
    time: `${pad(moment.getHours())}:${pad(moment.getMinutes())}:${pad(moment.getSeconds())}`,
  };
}

/** 2026-08-20. */
export function formatDate(value: string | null | undefined, fallback = ''): string {
  const formatted = value ? parts(value) : null;

  return formatted ? formatted.date : fallback;
}

/** 2026-08-20 16:41. */
export function formatDateTime(value: string | null | undefined, fallback = ''): string {
  const formatted = value ? parts(value) : null;

  return formatted ? `${formatted.date} ${formatted.time.slice(0, 5)}` : fallback;
}

/** 2026-08-20 16:41:55, for the places where the seconds are the point. */
export function formatTimestamp(value: string | null | undefined, fallback = ''): string {
  const formatted = value ? parts(value) : null;

  return formatted ? `${formatted.date} ${formatted.time}` : fallback;
}

const BYTE_UNITS = ['byte', 'kilobyte', 'megabyte', 'gigabyte'] as const;

/** 1.2 MB, or 1,2 Mo in French: the unit names come from the browser's own formatter. */
export function formatBytes(bytes: number, locale?: string): string {
  let value = bytes;
  let unit = 0;
  while (value >= 1024 && unit < BYTE_UNITS.length - 1) {
    value /= 1024;
    unit++;
  }

  return new Intl.NumberFormat(locale, {
    style: 'unit',
    unit: BYTE_UNITS[unit],
    unitDisplay: 'short',
    maximumFractionDigits: unit === 0 ? 0 : 1,
  }).format(value);
}
